import { Typography } from '@riac/design-system';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
dayjs.extend(advancedFormat);

import React from 'react';
import { IconClock } from '@riac/design-system';

const defaults = {
  startDateFormat: 'MMMM Do',
  endDateFormat: 'MMMM Do, YYYY',
  timeFormat: 'h:mm a',
};

// We want at least these fields, but we will accept(and ignore) others
export type AuctionData = {
  AuctionID: number | string,
  StartDate: string,
  EndDate: string,
  StartTime: string,
  [field: string]: any,
};

export type AuctionDateProps = React.PropsWithChildren<{
  auction: AuctionData,
  startDateFormat?: string,
  dateSpacer?: string,
  endDateFormat?: string,
  timeSpacer?: string,
  timeFormat?: string,
  omitTime?: boolean,
  gap?: string,
  italic?: boolean,
  bold?: boolean,
  class?: string,
  style?: Record<string, any>,
  justifyContent?: string,
  omitIcon?: boolean;
}>;

/* NOTE: We need to keep this component simple.  Avoid the urge to kitchen sink it to
* accomodate any possible combination of dates/times.
*
* The original problem this component aims to solve is that during the redesign
* we incurred a regression where a page that used to display the full date range
* for multi day auctions was only showing the start date.
* This component handles the logic to determine display of single date or date range
* and it will display it in a standardized layout so we can use it and avoid future regressions.
*
* [StartDate][DateSep][EndDate][TimeSep][Time]
*
* If you can't get the display you are going for through the props or styled components
* Then your use case probably isn't appropriate for this component.
*/
export const AuctionDate = (props: AuctionDateProps): JSX.Element => {
  // This constructs dayjs objects in Central time from a date with no specified timezone.
  // StartDate and EndDate is in the format YYYY-MM-DD
  // StartTime is in the format HH:mm:ss
  
  const start = dayjs.tz(props.auction.StartDate + 'T' + props.auction.StartTime, 'America/Chicago');
  const end = dayjs.tz(props.auction.EndDate + 'T' + '11:59:59', 'America/Chicago');
  const isMultiDay = end.diff(start, 'day') > 0;

  const dateSpacer = props.dateSpacer || ' - ';

  const startDateFormat = props.startDateFormat || defaults.startDateFormat;
  const endDateFormat = props.endDateFormat || defaults.endDateFormat;
  const timeFormat = props.timeFormat || defaults.timeFormat;

  return (
    <>
      {!props.omitIcon && <IconClock className='inline-block h-6 w-6 align-text-top mr-1'/>}
      {isMultiDay
        ? (
          <>
            {start.format(startDateFormat)}
            {dateSpacer}
            {end.format(endDateFormat)}
          </>
        )
        : (
          <>{start.format(endDateFormat)}</>
        )
      }
      {(!props.omitTime && props.timeSpacer) && (
        <>{props.timeSpacer}</>
      )}
      {!props.omitTime && (
        <>{start.format(timeFormat)}</>
      )}
    </>
  );
};